/**
 * Small, dependency-free Markdown renderer for LLM answers and source extracts.
 *
 * Safety model: the input is escaped first, then a fixed set of tags is
 * re-introduced. No user/model text can ever reach the DOM as markup, so the
 * result is safe to bind with `[innerHTML]`.
 *
 * Replaces the two divergent pipelines of the original application (one in the
 * message component, one in a `markdown` pipe) with a single implementation.
 */

export interface MarkdownOptions {
  /**
   * Number of available sources. When > 0, citation markers such as `[1]` or
   * `[1, 3]` become hoverable spans carrying `data-citation-indices`
   * (0-based, comma-separated). Markers pointing outside the range are left as
   * plain text — the model does hallucinate numbers.
   */
  sourceCount?: number;
  /**
   * Remove the trailing "Sources :" block the generation prompt appends. It
   * duplicates the structured `sources[]` the widget renders as cards.
   */
  stripSourcesSection?: boolean;
  /** Accessible label prefix for citation markers. */
  citationLabel?: string;
  /**
   * Lightweight pass used while an answer is still streaming. Skips tables,
   * lists, blockquotes and headings — features that are incomplete mid-stream
   * and expensive relative to the per-token update rate. Inline formatting,
   * links and line breaks still apply.
   */
  streaming?: boolean;
}

/**
 * Trailing source block emitted by the backend system prompt: a `Source(s) :`
 * header followed by `[n] [title](link)` or `- [n](link)` lines. Best-effort
 * heuristic over generated text, not a strict parser.
 */
const SOURCES_SECTION_RE =
  /\n{1,2}\**\s*Sources?\s*:\s*\**[ \t]*\r?\n(?:[ \t]*(?:[-*]\s*)?\[[^\]\n]*\][^\n]*\r?\n?)+\s*$/i;

/** Digits only, one or more comma-separated indices: `[1]`, `[1, 4]`, `[1,3]`. */
const CITATION_RE = /\[(\d+(?:\s*,\s*\d+)*)\](?!\()/g;

const CODE_PLACEHOLDER = ' rag-code-';

export function renderMarkdown(input: string | null | undefined, options: MarkdownOptions = {}): string {
  if (!input) return '';

  let text = input;
  if (options.stripSourcesSection && !options.streaming) {
    text = text.replace(SOURCES_SECTION_RE, '').trimEnd();
  }

  text = escapeHtml(text);

  // Pull fenced code out of the way so later passes cannot mangle its content.
  // During streaming, incomplete fences are left alone so partial code does
  // not disappear until the closing ``` arrives.
  const codeBlocks: string[] = [];
  if (!options.streaming) {
    text = text.replace(/```([A-Za-z0-9_+-]+)?\r?\n?([\s\S]*?)```/g, (_match, lang: string | undefined, code: string) => {
      const langAttr = lang ? ` class="language-${lang}"` : '';
      codeBlocks.push(`<pre><code${langAttr}>${code.replace(/\r?\n$/, '')}</code></pre>`);
      return `${CODE_PLACEHOLDER}${codeBlocks.length - 1}\0`;
    });
  }

  if (!options.streaming) {
    text = renderTables(text);
  }
  text = renderMarkdownLinks(text);
  text = renderBareUrls(text);
  if (options.sourceCount && !options.streaming) {
    text = renderCitations(text, options.sourceCount, options.citationLabel ?? 'Source');
  }
  if (!options.streaming) {
    text = renderHeadings(text);
    text = renderBlockquotes(text);
    text = renderLists(text);
  }
  text = renderInline(text);
  text = renderLineBreaks(text);

  // Restore code blocks (they are block-level, so drop the <br/> glued to them).
  if (codeBlocks.length) {
    text = text.replace(
      new RegExp(`${CODE_PLACEHOLDER}(\\d+)\0(<br/>)?`, 'g'),
      (_m, index: string) => codeBlocks[Number(index)] ?? '',
    );
  }

  return options.streaming ? text : tidyLineBreaks(text);
}

/**
 * Block elements bring their own margins, so the `<br/>` the line pass leaves
 * around them is redundant — and the one at the very end added a phantom blank
 * line at the bottom of every message.
 */
const BLOCK_TAGS = 'ul|ol|table|pre|blockquote|h[1-6]';

function tidyLineBreaks(html: string): string {
  return html
    .replace(new RegExp(`(?:<br/>)+(?=<(?:${BLOCK_TAGS})[\\s>])`, 'g'), '')
    .replace(new RegExp(`(?<=</(?:${BLOCK_TAGS})>)(?:<br/>)+`, 'g'), '')
    .replace(/(?:<br\/>)+$/, '');
}

const ESCAPE_RE = /[&<>"']/g;
const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(text: string): string {
  // Fast path: most streaming deltas contain no special characters.
  // Use indexOf rather than a global `.test()` so we never leave `lastIndex` dirty.
  if (
    text.indexOf('&') === -1 &&
    text.indexOf('<') === -1 &&
    text.indexOf('>') === -1 &&
    text.indexOf('"') === -1 &&
    text.indexOf("'") === -1
  ) {
    return text;
  }
  return text.replace(ESCAPE_RE, (m) => ESCAPE_MAP[m]!);
}

// ─── Block level ──────────────────────────────────────────────────────────────

function renderHeadings(text: string): string {
  return text.replace(/^(#{1,6})\s+(.*?)\s*$/gm, (_m, hashes: string, content: string) => {
    const level = hashes.length;
    return `<h${level} class="rag-md-h">${content}</h${level}>`;
  });
}

function renderBlockquotes(text: string): string {
  return text.replace(/^(?:&gt;)\s?(.*)$/gm, '<blockquote>$1</blockquote>');
}

/**
 * Bullet (`-`, `*`, `+`) and ordered (`1.`) lists, with one nesting level.
 *
 * The original implementation only recognised `* ` bullets, so the `-` and
 * `1.` lists the model actually produces rendered as raw text.
 */
function renderLists(text: string): string {
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  /** Open list elements, innermost last. */
  const stack: { tag: 'ul' | 'ol'; indent: number }[] = [];

  const closeTo = (indent: number): void => {
    while (stack.length && stack[stack.length - 1]!.indent >= indent) {
      out.push(`</${stack.pop()!.tag}>`);
    }
  };
  const closeAll = (): void => closeTo(-1);

  for (const line of lines) {
    const match = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
    if (!match) {
      if (line.trim() === '' && stack.length) {
        // Blank line inside a list: keep the list open, emit nothing.
        continue;
      }
      closeAll();
      out.push(`${line}<br/>`);
      continue;
    }

    const [, rawIndent, bullet, , content] = match;
    const indent = (rawIndent ?? '').replace(/\t/g, '  ').length;
    const tag: 'ul' | 'ol' = bullet ? 'ul' : 'ol';
    const top = stack[stack.length - 1];

    if (!top || indent > top.indent) {
      stack.push({ tag, indent });
      out.push(`<${tag}>`);
    } else {
      closeTo(indent + 1);
      const current = stack[stack.length - 1];
      if (!current) {
        stack.push({ tag, indent });
        out.push(`<${tag}>`);
      } else if (current.tag !== tag) {
        out.push(`</${current.tag}>`);
        stack[stack.length - 1] = { tag, indent };
        out.push(`<${tag}>`);
      }
    }
    out.push(`<li>${content}</li>`);
  }

  closeAll();
  return out.join('');
}

/** GFM pipe tables. Must run before list/line-break passes fragment the lines. */
function renderTables(text: string): string {
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const header = lines[i];
    const separator = lines[i + 1];

    if (header?.includes('|') && separator !== undefined && isSeparatorRow(separator)) {
      const headerCells = splitRow(header);
      const bodyRows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j]!.trim() !== '' && lines[j]!.includes('|')) {
        bodyRows.push(splitRow(lines[j]!));
        j++;
      }
      const thead = `<thead><tr>${headerCells.map((c) => `<th>${c}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map((row) => `<tr>${row.map((c) => `<td>${c}</td>`).join('')}</tr>`)
        .join('')}</tbody>`;
      out.push(`<table class="rag-md-table">${thead}${tbody}</table>`);
      i = j;
    } else {
      out.push(lines[i]!);
      i++;
    }
  }

  return out.join('\n');
}

function splitRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((cell) => cell.trim());
}

function isSeparatorRow(line: string): boolean {
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(cell));
}

// ─── Inline level ─────────────────────────────────────────────────────────────

function renderInline(text: string): string {
  return text
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    // Skip `*` at a line start followed by a space (that is a bullet, already
    // consumed) and require non-space edges so `a * b` is left alone.
    .replace(/(?<!\*)\*(?!\s)([^*\n]+)(?<!\s)\*(?!\*)/g, '<em>$1</em>');
}

function renderMarkdownLinks(text: string): string {
  return text.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g,
    (_m, label: string, url: string) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="rag-link">${label}</a>`,
  );
}

function renderBareUrls(text: string): string {
  const anchorSplit = /(<a\s[^>]*>[\s\S]*?<\/a>)/gi;
  const urlRe = /\b(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/gi;
  return text
    .split(anchorSplit)
    .map((part) =>
      part.toLowerCase().startsWith('<a ')
        ? part
        : part.replace(
            urlRe,
            (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="rag-link">${url}</a>`,
          ),
    )
    .join('');
}

function renderCitations(text: string, sourceCount: number, label: string): string {
  return text.replace(CITATION_RE, (full: string, group: string) => {
    const indices = group
      .split(',')
      .map((n) => Number.parseInt(n.trim(), 10) - 1)
      .filter((i) => Number.isInteger(i) && i >= 0 && i < sourceCount);

    if (indices.length === 0) return full;

    const aria = `${label} ${indices.map((i) => i + 1).join(', ')}`;
    return (
      `<span class="rag-citation" tabindex="0" role="button" aria-haspopup="true" aria-expanded="false" ` +
      `aria-label="${aria}" data-citation-indices="${indices.join(',')}">${full}</span>`
    );
  });
}

function renderLineBreaks(text: string): string {
  return text.replace(/(\r\n|\r|\n)/g, '<br/>');
}
